import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";
import {
	Alert,
	Box,
	Checkbox,
	Chip,
	Collapse,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	styled,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";

import { actualizarRolesYDepartamentos } from "../../../services/apis";
import { notifyRouteAccessChanged } from "../../../services/routeAccess";

const StickyHeaderCell = styled(TableCell)(({ theme }) => ({
	position: "sticky",
	left: 0,
	zIndex: 3,
	minWidth: 220,
	backgroundColor: theme.palette.grey[100],
	borderRight: `1px solid ${theme.palette.divider}`,
}));

const StickyBodyCell = styled(TableCell)(({ theme }) => ({
	position: "sticky",
	left: 0,
	zIndex: 2,
	minWidth: 220,
	backgroundColor: theme.palette.common.white,
	borderRight: `1px solid ${theme.palette.divider}`,
}));

const normalizeSearchValue = (value) =>
	String(value || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();

const getSaveStateMeta = (saveState) => {
	switch (saveState) {
		case "pending":
			return { color: "warning", label: "Cambios pendientes" };
		case "saving":
			return { color: "info", label: "Guardando..." };
		case "saved":
			return { color: "success", label: "Guardado" };
		case "error":
			return { color: "error", label: "Error al guardar" };
		default:
			return { color: "default", label: "Sin cambios" };
	}
};

export const PermisosOpciones = ({
	roles,
	opciones,
	title,
	nombreArray,
	descripcion,
	index,
	onUpdatePermissions,
}) => {
	const [opcionesTemp, setOpcionesTemp] = useState(Array.isArray(opciones) ? [...opciones] : []);
	const [open, setOpen] = useState(index === 0);
	const [search, setSearch] = useState("");
	const [saveState, setSaveState] = useState("idle");
	const [saveError, setSaveError] = useState("");
	const resetStateTimeoutRef = useRef(null);

	useEffect(() => {
		setOpcionesTemp(Array.isArray(opciones) ? [...opciones] : []);
	}, [opciones]);

	const actualizarPermisos = useCallback(
		async (updatedOpciones, rolId) => {
			setSaveState("saving");
			setSaveError("");

			try {
				if (typeof onUpdatePermissions === "function") {
					await onUpdatePermissions(updatedOpciones, rolId);
				} else {
					await actualizarRolesYDepartamentos(updatedOpciones, nombreArray, rolId);
				}

				notifyRouteAccessChanged();
				setSaveState("saved");
				if (resetStateTimeoutRef.current) {
					clearTimeout(resetStateTimeoutRef.current);
				}
				resetStateTimeoutRef.current = setTimeout(() => {
					setSaveState("idle");
				}, 1800);
			} catch (error) {
				console.error("Error guardando permisos:", error);
				setSaveState("error");
				setSaveError(
					error?.message || "No se pudieron guardar los permisos de este bloque."
				);
			}
		},
		[nombreArray, onUpdatePermissions]
	);

	const debouncedPersistPermissions = useMemo(
		() => debounce(actualizarPermisos, 700),
		[actualizarPermisos]
	);

	useEffect(() => {
		return () => {
			debouncedPersistPermissions.cancel();
			if (resetStateTimeoutRef.current) {
				clearTimeout(resetStateTimeoutRef.current);
			}
		};
	}, [debouncedPersistPermissions]);

	const visibleOpciones = useMemo(() => {
		const normalizedSearch = normalizeSearchValue(search);

		if (!normalizedSearch) {
			return opcionesTemp;
		}

		return opcionesTemp.filter((opcion) =>
			normalizeSearchValue(opcion?.nombre).includes(normalizedSearch)
		);
	}, [opcionesTemp, search]);

	const handleCheckboxChange = (rolId, opcionId) => {
		setOpcionesTemp((currentOpciones) => {
			const updatedOpciones = currentOpciones.map((opcion) => {
				if (opcion.id !== opcionId) {
					return opcion;
				}

				const isRolPresent = opcion.roles_permitidos.includes(rolId);
				const updatedRolesPermitidos = isRolPresent
					? opcion.roles_permitidos.filter((id) => id !== rolId)
					: [...opcion.roles_permitidos, rolId].sort((left, right) => left - right);

				return {
					...opcion,
					roles_permitidos: updatedRolesPermitidos,
				};
			});

			setSaveState("pending");
			setSaveError("");
			debouncedPersistPermissions(updatedOpciones, rolId);

			return updatedOpciones;
		});
	};

	const saveStateMeta = getSaveStateMeta(saveState);
	const totalAssignments = useMemo(
		() =>
			opcionesTemp.reduce(
				(total, opcion) => total + Number(opcion?.roles_permitidos?.length || 0),
				0
			),
		[opcionesTemp]
	);

	if (!Array.isArray(opciones) || opciones.length === 0) {
		return null;
	}

	return (
		<Grid container size={{ xs: 12 }} sx={{ mb: 2.5 }}>
			<Paper
				variant="outlined"
				sx={{
					width: "100%",
					borderRadius: 2,
					overflow: "hidden",
					borderColor: "divider",
					boxShadow: "none",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: { xs: "flex-start", md: "center" },
						justifyContent: "space-between",
						gap: 2,
						px: 2,
						py: 1.75,
						bgcolor: open ? "grey.50" : "common.white",
						borderBottom: open ? "1px solid" : "none",
						borderColor: "divider",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
						<IconButton size="small" onClick={() => setOpen((current) => !current)}>
							{open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
						</IconButton>

						<Box sx={{ minWidth: 0 }}>
							<Typography variant="h6" sx={{ fontSize: "1.05rem", fontWeight: 700 }}>
								{title}
							</Typography>
							{descripcion && (
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
									{descripcion}
								</Typography>
							)}
						</Box>
					</Box>

					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							gap: 0.75,
							justifyContent: "flex-end",
						}}
					>
						<Chip size="small" variant="outlined" label={`${roles.length} roles`} />
						<Chip size="small" variant="outlined" label={`${opcionesTemp.length} opciones`} />
						<Chip size="small" variant="outlined" label={`${totalAssignments} permisos`} />
						<Chip size="small" color={saveStateMeta.color} label={saveStateMeta.label} />
					</Box>
				</Box>

				<Collapse in={open}>
					<Box sx={{ p: 2 }}>
						<Box
							sx={{
								display: "flex",
								flexDirection: { xs: "column", lg: "row" },
								alignItems: { xs: "stretch", lg: "center" },
								justifyContent: "space-between",
								gap: 1.5,
								mb: 1.5,
							}}
						>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
								<SaveOutlinedIcon fontSize="small" color="action" />
								<Typography variant="body2" color="text.secondary">
									Los cambios se guardan automaticamente al marcar o desmarcar permisos.
								</Typography>
							</Box>

							{opcionesTemp.length > 8 && (
								<TextField
									size="small"
									placeholder="Buscar opcion..."
									value={search}
									onChange={(event) => setSearch(event.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon fontSize="small" />
											</InputAdornment>
										),
									}}
									sx={{ minWidth: { xs: "100%", lg: 280 } }}
								/>
							)}
						</Box>

						{saveError && (
							<Alert severity="error" sx={{ mb: 1.5 }}>
								{saveError}
							</Alert>
						)}

						{visibleOpciones.length === 0 ? (
							<Alert severity="info">No hay opciones que coincidan con la busqueda.</Alert>
						) : (
							<TableContainer
								component={Paper}
								variant="outlined"
								sx={{
									maxWidth: "100%",
									overflow: "auto",
									borderRadius: 1.5,
									borderColor: "divider",
								}}
							>
								<Table
									size="small"
									stickyHeader
									sx={{
										minWidth: Math.max(820, 220 + visibleOpciones.length * 170),
									}}
								>
									<TableHead>
										<TableRow>
											<StickyHeaderCell>
												<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
													Roles
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Activa la visibilidad por opcion
												</Typography>
											</StickyHeaderCell>

											{visibleOpciones.map((opcion) => (
												<TableCell
													key={opcion.id}
													align="center"
													sx={{
														bgcolor: "grey.100",
														minWidth: 170,
														verticalAlign: "top",
													}}
												>
													<Typography
														variant="subtitle2"
														sx={{ fontWeight: 700, mb: opcion?.descripcion ? 0.35 : 0 }}
													>
														{opcion.nombre}
													</Typography>
													{opcion?.descripcion && (
														<Typography
															variant="caption"
															color="text.secondary"
															sx={{ display: "block", lineHeight: 1.35 }}
														>
															{opcion.descripcion}
														</Typography>
													)}
												</TableCell>
											))}
										</TableRow>
									</TableHead>

									<TableBody>
										{roles.map((rol, roleIndex) => {
											const rowBackgroundColor =
												roleIndex % 2 === 0 ? "common.white" : "grey.50";
											const visibleCount = visibleOpciones.reduce((total, opcion) => {
												return total + (opcion.roles_permitidos.includes(Number(rol.id)) ? 1 : 0);
											}, 0);

											return (
												<TableRow key={rol.id} hover>
													<StickyBodyCell sx={{ bgcolor: rowBackgroundColor }}>
														<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
															<Typography variant="body2" sx={{ fontWeight: 700 }}>
																{rol.nombre}
															</Typography>
															<Box
																sx={{
																	display: "flex",
																	alignItems: "center",
																	gap: 0.75,
																	flexWrap: "wrap",
																}}
															>
																<CheckCircleOutlineIcon
																	fontSize="inherit"
																	sx={{ color: "text.secondary", fontSize: 14 }}
																/>
																<Typography variant="caption" color="text.secondary">
																	{visibleCount} de {visibleOpciones.length} visibles
																</Typography>
															</Box>
														</Box>
													</StickyBodyCell>

													{visibleOpciones.map((opcion) => (
														<TableCell
															key={`${rol.id}-${opcion.id}`}
															align="center"
															sx={{ bgcolor: rowBackgroundColor }}
														>
															<Checkbox
																size="small"
																checked={opcion.roles_permitidos.includes(Number(rol.id))}
																onChange={() =>
																	handleCheckboxChange(Number(rol.id), opcion.id)
																}
															/>
														</TableCell>
													))}
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Box>
				</Collapse>
			</Paper>
		</Grid>
	);
};
